import { useCallback } from 'react';
import type { TableSortingState } from '@ibsys/kit';
import type { TableSettings } from '../types';
import { useSettings } from '../useSettings';
import type { DriverState } from './types';

/**
 * Драйвер персистенции состояния таблицы через localStorage.
 *
 * Читает и записывает данные через {@link useSettings}, которая хранит
 * настройки таблиц в Zustand-сторе с персистенцией в localStorage.
 * Состояние привязано к текущему пользователю.
 *
 * @param defaultSorting - Сортировка по умолчанию, применяется только если пользователь ещё не задавал сортировку (значение не сохранено в localStorage)
 */
export const useLocalStorageDriver = (name: string, defaultSorting?: TableSortingState): DriverState => {
  const { tables, setTableSettings } = useSettings();
  const saved = tables[name] ?? {};

  const write = useCallback(
    (patch: Partial<TableSettings>) => setTableSettings(name, patch),
    [name, setTableSettings],
  );

  return {
    filters: saved.filters ?? [],
    sorting: saved.sorting ?? defaultSorting ?? [],
    offset: saved.offset,
    search: saved.search,
    contentSearch: saved.contentSearch ?? undefined,
    columnOrder: saved.columnOrder,
    columnVisibility: saved.columnVisibility,
    columnPinning: saved.columnPinning,
    columnSizing: saved.columnSizing,
    columnsPanelWidth: saved.columnsPanelWidth,
    columnsPanelExpandedGroups: saved.columnsPanelExpandedGroups,
    activeSidePanel: saved.activeSidePanel,
    sidePanelPinned: saved.sidePanelPinned,
    headerOrientation: saved.headerOrientation,
    scrollIndex: saved.scrollIndex,
    scrollKey: saved.scrollKey,
    dismissedSelectionKey: saved.dismissedSelectionKey,
    selectionDescription: saved.selectionDescription,
    write,
    path: `settings.${name}`,
  };
};
